import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Medal, Award, Users, Package, Box, Target, RefreshCw, MapPin, ScanLine, ClipboardList, ChevronRight, ChevronDown, Sun, Moon, Clock, TrendingUp, Zap, Layers, Lock, Eye, EyeOff, Calendar, Search, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ItemBreakdownTree from '@/components/reports/ItemBreakdownTree';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MobileStaffRankings = () => {
  const navigate = useNavigate();

  // PIN gate state
  const [pinVerified, setPinVerified] = useState(false);
  const [pin, setPin] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [pinError, setPinError] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  // 2026-02 — Site selector (Salem default, supports multi-section
  // sites like Srivilliputhur). Saved to localStorage.active_site_id so
  // the global axios interceptor in /services/apiClient.js attaches the
  // right X-Site-Id to every backend call.
  const [siteId, setSiteId] = useState(() =>
    localStorage.getItem('active_site_id') || 'salem'
  );
  const [siteOptions, setSiteOptions] = useState([
    { site_id: 'salem', site_name: 'Salem', is_default: true },
  ]);

  // Per-section sites (Srivilliputhur etc) aggregate the 3 dispatch
  // sections (color_combo, art_silk, kids) via the rollup endpoint.
  const isPerSectionSite = siteId !== 'salem';

  // Dashboard state
  const [drillTab, setDrillTab] = useState(null);
  const [period, setPeriod] = useState('daily');
  const [shift, setShift] = useState('all');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [rankings, setRankings] = useState(null);
  const [rankingsLoading, setRankingsLoading] = useState(true);
  const [piecesData, setPiecesData] = useState([]);
  const [piecesLoading, setPiecesLoading] = useState(false);
  const [packingData, setPackingData] = useState([]);
  const [packingLoading, setPackingLoading] = useState(false);
  const [pickSlipData, setPickSlipData] = useState(null);
  const [pickSlipLoading, setPickSlipLoading] = useState(false);
  // 2026-02 — Aggregated tile counts when active site rolls up multiple
  // per-section dispatch sections (Srivilliputhur). Shape:
  //   { sfg_put_away: {qty, staff_count, per_section: {...}}, ... }
  const [rollupOverview, setRollupOverview] = useState(null);
  const [rollupLoading, setRollupLoading] = useState(false);
  const [expandedStates, setExpandedStates] = useState({});
  const [pickSlipSearch, setPickSlipSearch] = useState('');
  // 2026-05-20 — Lazy-loaded item-level breakdown (Category 4 → Item → Staff).
  // One cache per op so each drill loads independently on first open.
  const [breakdowns, setBreakdowns] = useState({});  // { [op]: data | null }
  const [breakdownLoading, setBreakdownLoading] = useState({});  // { [op]: bool }

  const tabsMeta = [
    { key: 'sfg_put_away', label: 'SFG Put Away', icon: MapPin, gradient: 'from-orange-500 to-amber-600', border: 'border-orange-500/30', bg: 'bg-orange-950/40', unit: 'items' },
    { key: 'sfg_picker', label: 'SFG Picking', icon: Package, gradient: 'from-indigo-500 to-violet-600', border: 'border-indigo-500/30', bg: 'bg-indigo-950/40', unit: 'qty' },
    { key: 'pieces_processed', label: 'Pieces Processed', icon: ScanLine, gradient: 'from-teal-500 to-emerald-600', border: 'border-teal-500/30', bg: 'bg-teal-950/40', unit: 'pieces' },
    { key: 'put_away', label: 'FG Put Away', icon: Target, gradient: 'from-purple-500 to-fuchsia-600', border: 'border-purple-500/30', bg: 'bg-purple-950/40', unit: 'items' },
    { key: 'fg_pick_slip', label: 'FG Pick Slip', icon: ClipboardList, gradient: 'from-rose-500 to-pink-600', border: 'border-rose-500/30', bg: 'bg-rose-950/40', unit: 'qty' },
    { key: 'fg_picker', label: 'FG Picking', icon: Box, gradient: 'from-emerald-500 to-green-600', border: 'border-emerald-500/30', bg: 'bg-emerald-950/40', unit: 'qty' },
    { key: 'fg_packing', label: 'FG Packing', icon: Layers, gradient: 'from-cyan-500 to-sky-600', border: 'border-cyan-500/30', bg: 'bg-cyan-950/40', unit: 'pieces' },
  ];

  // ─── PIN verification ─────────────────────────────
  useEffect(() => {
    const saved = sessionStorage.getItem('dispatch_report_pin_verified');
    if (saved === 'true') setPinVerified(true);
    setCheckingSession(false);
  }, []);

  // 2026-02 — Load site list for the selector. Only `salem` and any
  // active multi-section site (e.g. Srivilliputhur) are shown — internal
  // canary/test sites are filtered out.
  useEffect(() => {
    axios.get(`${API_BASE}/sites-registry/public`)
      .then((r) => {
        const visible = (r.data?.sites || []).filter(
          (s) => s.site_id && !s.site_id.startsWith('__')
        );
        if (visible.length) setSiteOptions(visible);
      })
      .catch(() => {});
  }, []);

  const handleSiteChange = (newSiteId) => {
    if (newSiteId === siteId) return;
    setSiteId(newSiteId);
    localStorage.setItem('active_site_id', newSiteId);
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (!pin.trim()) return;
    setPinLoading(true);
    setPinError('');
    try {
      await axios.post(`${API_BASE}/management/verify-pin`, { pin: pin.trim() });
      sessionStorage.setItem('dispatch_report_pin_verified', 'true');
      setPinVerified(true);
      toast.success('Access granted');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Verification failed';
      setPinError(msg);
    } finally {
      setPinLoading(false);
    }
  };

  // ─── Data fetching (uses raw axios, no auth needed) ───
  const dateParams = period === 'custom' && customDateFrom && customDateTo
    ? `&date_from=${customDateFrom}&date_to=${customDateTo}` : '';

  const fetchRankings = useCallback(async () => {
    setRankingsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/wms/staff/rankings?period=${period}&shift=${shift}${dateParams}`);
      setRankings(res.data);
    } catch (e) { console.error('Rankings fetch failed:', e); }
    finally { setRankingsLoading(false); }
  }, [period, shift, dateParams]);

  const fetchPieces = useCallback(async () => {
    setPiecesLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/sections/dispatch/pieces-processed/all-staff-performance?period=${period}&shift=${shift}${dateParams}`);
      setPiecesData(res.data.staff_performance || []);
    } catch { setPiecesData([]); }
    finally { setPiecesLoading(false); }
  }, [period, shift, dateParams]);

  const fetchPacking = useCallback(async () => {
    setPackingLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/wms/packing/staff-performance?period=${period}&shift=${shift}${dateParams}`);
      setPackingData(res.data.staff_performance || []);
    } catch { setPackingData([]); }
    finally { setPackingLoading(false); }
  }, [period, shift, dateParams]);

  const fetchPickSlips = useCallback(async () => {
    setPickSlipLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/wms/fg/pick-slips/state-wise-performance?period=${period}&shift=${shift}${dateParams}`);
      setPickSlipData(res.data);
      setExpandedStates({});
    } catch { setPickSlipData(null); }
    finally { setPickSlipLoading(false); }
  }, [period, shift, dateParams]);

  // 2026-02 — Rollup fetch for multi-section sites (Srivilliputhur).
  // Sums activities of color_combo + art_silk + kids in one call.
  const fetchRollup = useCallback(async () => {
    setRollupLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/per-section-rollup/dispatch-overview?period=${period}&shift=${shift}${dateParams}`
      );
      setRollupOverview(res.data?.overview || null);
    } catch (e) {
      setRollupOverview(null);
    } finally {
      setRollupLoading(false);
    }
  }, [period, shift, dateParams]);

  // Lazy fetch the item-level breakdown for a given op. Endpoint differs:
  //   - pieces_processed → /sections/dispatch/pieces-processed/item-breakdown
  //   - sfg_put_away / sfg_picker / put_away / fg_picker / fg_packing
  //     → /wms/staff/rankings/item-breakdown?op=…
  // Cached per (period, shift) until filter changes invalidate it.
  const fetchBreakdown = useCallback(async (op) => {
    setBreakdownLoading((p) => ({ ...p, [op]: true }));
    try {
      let url;
      if (op === 'pieces_processed') {
        url = `${API_BASE}/sections/dispatch/pieces-processed/item-breakdown?period=${period}&shift=${shift}${dateParams}`;
      } else if (['sfg_put_away', 'sfg_picker', 'put_away', 'fg_picker', 'fg_packing'].includes(op)) {
        url = `${API_BASE}/wms/staff/rankings/item-breakdown?op=${op}&period=${period}&shift=${shift}${dateParams}`;
      } else {
        return;
      }
      const res = await axios.get(url);
      setBreakdowns((p) => ({ ...p, [op]: res.data }));
    } catch {
      setBreakdowns((p) => ({ ...p, [op]: null }));
    } finally {
      setBreakdownLoading((p) => ({ ...p, [op]: false }));
    }
  }, [period, shift, dateParams]);

  useEffect(() => {
    if (!pinVerified) return;
    // For multi-section sites (Srivilliputhur), the dispatch report
    // aggregates color_combo + art_silk + kids via the rollup endpoint.
    // Salem keeps using the monolithic rankings endpoints unchanged.
    if (isPerSectionSite) {
      fetchRollup();
      setRankings(null);
      setPiecesData([]);
      setPackingData([]);
      setPickSlipData(null);
    } else {
      fetchRankings();
      fetchPieces();
      fetchPacking();
      fetchPickSlips();
      setRollupOverview(null);
    }
    // Filter changed → invalidate ALL lazy breakdown caches so the next
    // drill open refetches with the new window.
    setBreakdowns({});
  }, [period, shift, pinVerified, siteId, isPerSectionSite, fetchRankings, fetchPieces, fetchPacking, fetchPickSlips, fetchRollup]);

  // Lazy-load the heavy breakdown only when the user opens a supporting drill.
  useEffect(() => {
    if (!pinVerified || !drillTab) return;
    const supported = ['pieces_processed', 'sfg_put_away', 'sfg_picker', 'put_away', 'fg_picker', 'fg_packing'];
    if (supported.includes(drillTab) && !breakdowns[drillTab] && !breakdownLoading[drillTab]) {
      fetchBreakdown(drillTab);
    }
  }, [drillTab, pinVerified, breakdowns, breakdownLoading, fetchBreakdown]);

  const refreshAll = () => {
    if (isPerSectionSite) {
      fetchRollup();
    } else {
      fetchRankings(); fetchPieces(); fetchPacking(); fetchPickSlips();
    }
    toast.success('Refreshed');
  };

  // ─── Helpers ──────────────────────────────────────
  const getOverviewQty = (key) => {
    // Multi-section rollup view (Srivilliputhur etc.) reads from the
    // single rollup payload so every tile shares one source of truth.
    if (isPerSectionSite) return rollupOverview?.[key]?.qty || 0;
    if (key === 'pieces_processed') return piecesData.reduce((s, d) => s + (d.total_pieces || 0), 0);
    if (key === 'fg_packing') return packingData.reduce((s, d) => s + (d.total_pieces || 0), 0);
    if (key === 'fg_pick_slip') return pickSlipData?.summary?.total_pick_slip_qty || 0;
    const staffArr = rankings?.rankings?.[key] || [];
    if (key === 'sfg_put_away' || key === 'put_away') return staffArr.reduce((s, d) => s + (d.total_items || 0), 0);
    return staffArr.reduce((s, d) => s + (d.total_qty || 0), 0);
  };

  const getStaffCount = (key) => {
    if (isPerSectionSite) return rollupOverview?.[key]?.staff_count || 0;
    if (key === 'pieces_processed') return piecesData.length;
    if (key === 'fg_packing') return packingData.length;
    if (key === 'fg_pick_slip') return pickSlipData?.states?.length || 0;
    return (rankings?.rankings?.[key] || []).length;
  };

  const isLoading = isPerSectionSite
    ? rollupLoading
    : (rankingsLoading || piecesLoading || packingLoading || pickSlipLoading);

  const getRankIcon = (index) => {
    if (index === 0) return <Trophy className="w-5 h-5 text-yellow-400" />;
    if (index === 1) return <Medal className="w-5 h-5 text-gray-300" />;
    if (index === 2) return <Award className="w-5 h-5 text-amber-500" />;
    return <span className="text-sm font-bold text-gray-400">#{index + 1}</span>;
  };

  // ─── PIN Gate Screen ──────────────────────────────
  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!pinVerified) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center px-6" data-testid="pin-gate">
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-xl font-bold">Dispatch Section Reports</h1>
            <p className="text-gray-400 text-sm mt-1">Enter PIN to access</p>
          </div>

          <form onSubmit={handlePinSubmit} className="space-y-4">
            <div className="relative">
              <input
                type={showPin ? 'text' : 'password'}
                inputMode="numeric"
                value={pin}
                onChange={(e) => { setPin(e.target.value.replace(/\D/g, '').slice(0, 8)); setPinError(''); }}
                placeholder="Enter 6-digit PIN"
                className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3.5 text-white text-center text-lg tracking-widest placeholder:text-gray-500 placeholder:tracking-normal focus:outline-none focus:border-emerald-500 transition-colors"
                autoFocus
                data-testid="pin-input"
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-300"
                data-testid="pin-toggle-visibility"
              >
                {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {pinError && (
              <p className="text-red-400 text-sm text-center" data-testid="pin-error">{pinError}</p>
            )}

            <button
              type="submit"
              disabled={pinLoading || !pin.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold py-3.5 rounded-xl transition-colors"
              data-testid="pin-submit"
            >
              {pinLoading ? 'Verifying...' : 'Access Reports'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Overview Screen ──────────────────────────────
  const renderOverview = () => (
    <div className="px-4 py-3 space-y-3">
      {tabsMeta.map((tab) => {
        const qty = getOverviewQty(tab.key);
        const staffCount = getStaffCount(tab.key);
        const TabIcon = tab.icon;
        return (
          <button
            key={tab.key}
            onClick={() => setDrillTab(tab.key)}
            className={`w-full ${tab.bg} border ${tab.border} rounded-2xl p-4 flex items-center gap-4 active:scale-[0.98] transition-all`}
            data-testid={`overview-card-${tab.key}`}
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tab.gradient} flex items-center justify-center flex-shrink-0`}>
              <TabIcon className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-white font-semibold text-sm">{tab.label}</p>
              <p className="text-gray-400 text-xs mt-0.5">
                {tab.key === 'fg_pick_slip' ? `${staffCount} states` : `${staffCount} staff`}
              </p>
            </div>
            <div className="text-right flex-shrink-0 mr-1">
              <p className="text-2xl font-bold text-white tabular-nums">{qty.toLocaleString()}</p>
              <p className="text-gray-500 text-xs">{tab.unit}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
          </button>
        );
      })}
    </div>
  );

  // ─── Drill-down: Core Rankings ────────────────────
  const renderCoreRankingDrill = () => {
    const tab = tabsMeta.find(t => t.key === drillTab);
    const staff = rankings?.rankings?.[drillTab] || [];
    const stats = rankings?.summary_stats?.[drillTab] || {};

    if (rankingsLoading) return <LoadingSpinner />;
    if (!staff.length) return <EmptyState text="No data for this period" />;

    const isItemBased = drillTab === 'sfg_put_away' || drillTab === 'put_away';
    const valueKey = isItemBased ? 'total_items' : 'total_qty';

    return (
      <div className="px-4 space-y-3">
        <div className="grid grid-cols-3 gap-2" data-testid={`drill-summary-${drillTab}`}>
          <div className={`${tab.bg} border ${tab.border} rounded-xl p-3`}>
            <Users className="w-4 h-4 text-gray-400 mb-1" />
            <p className="text-xl font-bold text-white">{stats.staff_count || staff.length}</p>
            <p className="text-gray-400 text-xs">Staff</p>
          </div>
          <div className={`${tab.bg} border ${tab.border} rounded-xl p-3`}>
            <TrendingUp className="w-4 h-4 text-gray-400 mb-1" />
            <p className="text-xl font-bold text-white">{(stats.total_volume || 0).toLocaleString()}</p>
            <p className="text-gray-400 text-xs">Volume</p>
          </div>
          <div className={`${tab.bg} border ${tab.border} rounded-xl p-3`}>
            <Zap className="w-4 h-4 text-gray-400 mb-1" />
            <p className="text-xl font-bold text-white">{stats.avg_speed || 0}</p>
            <p className="text-gray-400 text-xs">Avg Speed</p>
          </div>
        </div>
        {staff.map((s, i) => (
          <div key={s.staff_id || i} className="flex items-center gap-3 bg-gray-800 rounded-xl p-3" data-testid={`drill-rank-${i}`}>
            <div className="w-8 flex-shrink-0 flex items-center justify-center">{getRankIcon(i)}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white text-sm truncate">{s.staff_name || s.display_name || s.staff_id}</p>
              <p className="text-xs text-gray-400">{s.staff_id}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-lg font-bold text-emerald-400">{(s[valueKey] || 0).toLocaleString()}</p>
              {s.bins_visited !== undefined && <p className="text-xs text-gray-500">{s.bins_visited} bins</p>}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ─── Drill-down: Pieces Processed (uses shared 3-level tree) ──────
  const renderPiecesDrill = () => (
    <ItemBreakdownTree
      data={breakdowns.pieces_processed}
      loading={!!breakdownLoading.pieces_processed}
      accent="teal"
      testIdScope="pp"
    />
  );

  // ─── Drill-down: FG Packing ───────────────────────
  const renderPackingDrill = () => {
    if (packingLoading) return <LoadingSpinner />;
    if (!packingData.length) return <EmptyState text="No packing data" />;
    const totalPcs = packingData.reduce((s, d) => s + (d.total_pieces || 0), 0);
    const totalBoxes = packingData.reduce((s, d) => s + (d.total_boxes || 0), 0);
    return (
      <div className="px-4 space-y-3">
        <div className="grid grid-cols-3 gap-2" data-testid="drill-summary-fg_packing">
          <div className="bg-cyan-950/40 border border-cyan-500/30 rounded-xl p-3">
            <Users className="w-4 h-4 text-gray-400 mb-1" />
            <p className="text-xl font-bold text-white">{packingData.length}</p>
            <p className="text-gray-400 text-xs">Staff</p>
          </div>
          <div className="bg-cyan-950/40 border border-cyan-500/30 rounded-xl p-3">
            <ScanLine className="w-4 h-4 text-gray-400 mb-1" />
            <p className="text-xl font-bold text-white">{totalPcs.toLocaleString()}</p>
            <p className="text-gray-400 text-xs">Pieces</p>
          </div>
          <div className="bg-cyan-950/40 border border-cyan-500/30 rounded-xl p-3">
            <Box className="w-4 h-4 text-gray-400 mb-1" />
            <p className="text-xl font-bold text-white">{totalBoxes.toLocaleString()}</p>
            <p className="text-gray-400 text-xs">Boxes</p>
          </div>
        </div>
        {packingData.map((s, i) => (
          <div key={s.staff_id} className="flex items-center gap-3 bg-gray-800 rounded-xl p-3" data-testid={`drill-packing-${i}`}>
            <div className="w-8 flex-shrink-0 flex items-center justify-center">{getRankIcon(i)}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-white text-sm truncate">{s.staff_name}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-lg font-bold text-cyan-400">{(s.total_pieces || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500">{s.total_boxes || 0} boxes</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ─── Drill-down: FG Pick Slip ─────────────────────
  const renderPickSlipDrill = () => {
    if (pickSlipLoading) return <LoadingSpinner />;
    const summary = pickSlipData?.summary || {};
    const states = pickSlipData?.states || [];

    // Filter states/customers based on search (case-insensitive)
    const filteredStates = pickSlipSearch
      ? states.map(sg => {
          const q = pickSlipSearch.toLowerCase();
          const matchedCustomers = sg.customers.filter(c =>
            (c.customer || '').toLowerCase().includes(q) ||
            (c.agent || '').toLowerCase().includes(q)
          );
          if (sg.state.toLowerCase().includes(q)) return sg;
          if (matchedCustomers.length > 0) {
            return {
              ...sg,
              customers: matchedCustomers,
              customer_count: matchedCustomers.length,
              total_qty: matchedCustomers.reduce((s, c) => s + c.total_qty, 0),
              picked_qty: matchedCustomers.reduce((s, c) => s + c.picked_qty, 0)
            };
          }
          return null;
        }).filter(Boolean)
      : states;

    return (
      <div className="px-4 space-y-3">
        <div className="grid grid-cols-2 gap-2" data-testid="drill-summary-fg_pick_slip">
          <div className="col-span-2 bg-purple-900/60 border border-purple-700/40 rounded-xl p-3">
            <p className="text-purple-300 text-xs font-medium">Opening Balance (Carried Forward)</p>
            <p className="text-2xl font-bold text-white" data-testid="pick-slip-opening-balance">{(summary.opening_balance || 0).toLocaleString()}</p>
            <p className="text-purple-400 text-xs">Pending from previous periods</p>
          </div>
          <div className="bg-blue-900/60 border border-blue-700/40 rounded-xl p-3">
            <p className="text-blue-300 text-xs font-medium">Total Qty</p>
            <p className="text-2xl font-bold text-white">{(summary.total_pick_slip_qty || 0).toLocaleString()}</p>
          </div>
          <div className="bg-green-900/60 border border-green-700/40 rounded-xl p-3">
            <p className="text-green-300 text-xs font-medium">Picked</p>
            <p className="text-2xl font-bold text-white">{(summary.picked_qty || 0).toLocaleString()}</p>
          </div>
          <div className="bg-amber-900/60 border border-amber-700/40 rounded-xl p-3">
            <p className="text-amber-300 text-xs font-medium">In Progress</p>
            <p className="text-2xl font-bold text-white">{(summary.in_progress_qty || 0).toLocaleString()}</p>
          </div>
          <div className="bg-red-900/60 border border-red-700/40 rounded-xl p-3">
            <p className="text-red-300 text-xs font-medium">Pending</p>
            <p className="text-2xl font-bold text-white">{(summary.pending_qty || 0).toLocaleString()}</p>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            placeholder="Search state, customer or agent..."
            value={pickSlipSearch}
            onChange={(e) => setPickSlipSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500"
            data-testid="pick-slip-search-mobile"
          />
        </div>
        {filteredStates.length === 0 ? <EmptyState text={pickSlipSearch ? "No matching results" : "No pick slip data"} /> : (
          <div className="space-y-2">
            {filteredStates.map((sg) => (
              <div key={sg.state} className="bg-gray-800 rounded-xl overflow-hidden" data-testid={`drill-state-${sg.state}`}>
                <button
                  onClick={() => setExpandedStates(prev => ({ ...prev, [sg.state]: !prev[sg.state] }))}
                  className="w-full flex items-center justify-between p-3 active:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {expandedStates[sg.state] ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <MapPin className="w-4 h-4 text-rose-400" />
                    <span className="font-semibold text-white text-sm">{sg.state}</span>
                    <span className="text-xs text-gray-500">({sg.customer_count})</span>
                  </div>
                  <div className="flex items-center gap-2" data-testid={`state-picked-vs-total-${sg.state}`}>
                    <span className="text-lg font-bold text-green-400 tabular-nums">{(sg.picked_qty || 0).toLocaleString()}</span>
                    <span className="text-sm text-gray-500">/</span>
                    <span className="text-lg font-bold text-white tabular-nums">{sg.total_qty.toLocaleString()}</span>
                    <span className="text-xs text-gray-500">qty</span>
                  </div>
                </button>
                {expandedStates[sg.state] && (
                  <div className="border-t border-gray-700 divide-y divide-gray-700/50">
                    {sg.customers.map((c) => (
                      <div key={c.customer} className="flex items-center justify-between px-4 py-2.5 pl-10">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-200 truncate">{c.customer}</p>
                          {c.agent && <p className="text-xs text-gray-500">{c.agent}</p>}
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          <p className="text-sm font-bold text-white">{c.total_qty.toLocaleString()}</p>
                          <p className="text-xs text-green-400">{c.picked_qty.toLocaleString()} picked</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDrillContent = () => {
    // 2026-02 — Multi-section sites (Srivilliputhur) render a per-section
    // breakdown card instead of the staff-level item drilldown. The
    // existing tree-style breakdowns only exist for Salem's monolithic
    // collections; rolled-up views surface section-level totals so
    // the operator can navigate into a specific section for finer grain.
    if (isPerSectionSite) {
      const tile = rollupOverview?.[drillTab];
      const perSection = tile?.per_section || {};
      const sectionEntries = Object.entries(perSection);
      const sectionLabels = {
        color_combo: 'Color Combo',
        art_silk: 'Art Silk',
        kids: 'Kids',
      };
      const sectionAccents = {
        color_combo: 'from-rose-500 to-pink-600',
        art_silk: 'from-amber-500 to-orange-600',
        kids: 'from-sky-500 to-cyan-600',
      };
      if (rollupLoading) return <LoadingSpinner />;
      if (!tile || sectionEntries.length === 0) {
        return <EmptyState text="No activity in this window across sections" />;
      }
      return (
        <div className="px-4 space-y-3" data-testid={`rollup-drill-${drillTab}`}>
          <div className="bg-emerald-950/40 border border-emerald-500/30 rounded-xl p-4">
            <div className="text-emerald-200 text-xs font-medium uppercase tracking-wide">
              Combined activity ({sectionEntries.length} sections)
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <p className="text-3xl font-bold text-white">
                {(tile.qty || 0).toLocaleString()}
              </p>
              <p className="text-emerald-300 text-sm">
                · {tile.staff_count} staff
              </p>
            </div>
            <p className="text-emerald-100/70 text-xs mt-1.5">
              Aggregated across Color Combo, Art Silk &amp; Kids sections.
            </p>
          </div>
          <div className="text-xs text-gray-400 px-1">Per-section breakdown</div>
          {sectionEntries.map(([slug, data]) => (
            <button
              key={slug}
              onClick={() => navigate(`/sections/dispatch/${slug}`)}
              className="w-full bg-gray-800 rounded-xl p-3 flex items-center gap-3 active:scale-[0.98] transition-all"
              data-testid={`rollup-section-${slug}`}
            >
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${sectionAccents[slug] || 'from-gray-500 to-gray-600'} flex items-center justify-center flex-shrink-0`}>
                <Layers className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="font-semibold text-white text-sm">
                  {sectionLabels[slug] || slug}
                </p>
                <p className="text-xs text-gray-400">
                  {data.staff_count || 0} staff
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-white">
                  {(data.qty || 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">{activeMeta?.unit || 'pcs'}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-500" />
            </button>
          ))}
          <p className="text-xs text-gray-500 italic px-1 pt-2">
            Tap a section card to open its dispatch hub for finer-grain
            drill-downs.
          </p>
        </div>
      );
    }
    switch (drillTab) {
      // 2026-05-20 — All operational drills (SFG + FG) use the shared
      // ItemBreakdownTree component for consistent grouping by Category 4 →
      // Design–Shade–Size–Description → Staff.
      case 'sfg_put_away':
        return <ItemBreakdownTree data={breakdowns.sfg_put_away} loading={!!breakdownLoading.sfg_put_away} accent="amber" testIdScope="sfg-pa" />;
      case 'sfg_picker':
        return <ItemBreakdownTree data={breakdowns.sfg_picker} loading={!!breakdownLoading.sfg_picker} accent="indigo" testIdScope="sfg-pk" />;
      case 'put_away':
        return <ItemBreakdownTree data={breakdowns.put_away} loading={!!breakdownLoading.put_away} accent="emerald" testIdScope="fg-pa" />;
      case 'fg_picker':
        return <ItemBreakdownTree data={breakdowns.fg_picker} loading={!!breakdownLoading.fg_picker} accent="purple" testIdScope="fg-pk" />;
      case 'fg_packing':
        return <ItemBreakdownTree data={breakdowns.fg_packing} loading={!!breakdownLoading.fg_packing} accent="rose" testIdScope="fg-pack" />;
      case 'pieces_processed': return renderPiecesDrill();
      case 'fg_pick_slip': return renderPickSlipDrill();
      default: return null;
    }
  };

  const activeMeta = drillTab ? tabsMeta.find(t => t.key === drillTab) : null;

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col" data-testid="dispatch-section-reports">
      {/* Header */}
      <header className="bg-gray-800 px-4 py-3 flex items-center gap-3 sticky top-0 z-20">
        <button
          onClick={() => drillTab ? setDrillTab(null) : navigate(-1)}
          className="p-1.5 rounded-lg hover:bg-gray-700 active:bg-gray-600"
          data-testid="report-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          {drillTab ? (
            <>
              <h1 className="text-lg font-bold truncate">{activeMeta?.label}</h1>
              <p className="text-xs text-gray-400">Tap arrow to go back</p>
            </>
          ) : (
            <>
              <h1 className="text-lg font-bold">Dispatch Section Reports</h1>
              <p className="text-xs text-gray-400">Staff Performance Overview</p>
            </>
          )}
        </div>
        {/* 2026-02 — Site selector. Salem (default) keeps existing
            monolithic endpoints; multi-section sites (Srivilliputhur)
            switch the report to read from the rollup endpoint that
            sums color_combo + art_silk + kids. */}
        <div
          className="relative flex items-center gap-1.5 bg-gray-700/60 rounded-lg px-2 py-1 mr-1"
          data-testid="site-selector"
        >
          <Building2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          <select
            value={siteId}
            onChange={(e) => handleSiteChange(e.target.value)}
            className="bg-transparent text-white text-xs font-semibold focus:outline-none cursor-pointer pr-1 max-w-[110px]"
            data-testid="site-selector-dropdown"
          >
            {siteOptions.map((s) => (
              <option
                key={s.site_id}
                value={s.site_id}
                data-testid={`site-option-${s.site_id}`}
                className="bg-gray-800"
              >
                {s.site_name || s.site_id}
              </option>
            ))}
          </select>
        </div>
        <button onClick={refreshAll} className="p-2 rounded-lg hover:bg-gray-700 active:bg-gray-600" data-testid="report-refresh-btn">
          <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </header>

      {/* Period + Shift Filters */}
      <div className="bg-gray-800/50 px-4 py-2.5 flex items-center gap-2 border-b border-gray-700 overflow-x-auto">
        {['daily', 'yesterday', 'weekly', 'monthly'].map((p) => (
          <button
            key={p}
            onClick={() => { setPeriod(p); setShowDatePicker(false); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${period === p ? 'bg-emerald-600 text-white' : 'bg-gray-700 text-gray-300 active:bg-gray-600'}`}
            data-testid={`mobile-period-${p}`}
          >
            {p === 'daily' ? 'Today' : p === 'yesterday' ? 'Yesterday' : p === 'weekly' ? 'Week' : 'Month'}
          </button>
        ))}
        <button
          onClick={() => { setShowDatePicker(!showDatePicker); if (period !== 'custom') setPeriod('custom'); }}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${period === 'custom' ? 'bg-emerald-600 text-white' : 'bg-gray-700 text-gray-300 active:bg-gray-600'}`}
          data-testid="mobile-period-custom"
        >
          <Calendar className="w-3.5 h-3.5" />
          Custom
        </button>
        <div className="w-px h-6 bg-gray-600 mx-1" />
        {[
          { key: 'all', label: 'All', icon: Clock },
          { key: 'morning', label: 'Day', icon: Sun },
          { key: 'night', label: 'Night', icon: Moon }
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setShift(s.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${shift === s.key ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-gray-300 active:bg-gray-600'}`}
            data-testid={`mobile-shift-${s.key}`}
          >
            <s.icon className="w-3.5 h-3.5" />
            {s.label}
          </button>
        ))}
      </div>

      {/* Custom Date Picker */}
      {showDatePicker && period === 'custom' && (
        <div className="bg-gray-800/80 px-4 py-3 border-b border-gray-700 flex items-center gap-2 flex-wrap">
          <input
            type="date"
            value={customDateFrom}
            onChange={(e) => setCustomDateFrom(e.target.value)}
            className="bg-gray-700 text-white text-xs px-3 py-1.5 rounded-lg border border-gray-600 focus:border-emerald-500 focus:outline-none"
            data-testid="custom-date-from"
          />
          <span className="text-gray-400 text-xs">to</span>
          <input
            type="date"
            value={customDateTo}
            onChange={(e) => setCustomDateTo(e.target.value)}
            className="bg-gray-700 text-white text-xs px-3 py-1.5 rounded-lg border border-gray-600 focus:border-emerald-500 focus:outline-none"
            data-testid="custom-date-to"
          />
          <button
            onClick={() => { if (customDateFrom && customDateTo) { setPeriod('custom'); setShowDatePicker(false); } else { toast.error('Select both dates'); } }}
            disabled={!customDateFrom || !customDateTo}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white disabled:bg-gray-600 disabled:text-gray-400"
            data-testid="custom-date-apply"
          >
            Apply
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-8">
        {drillTab ? renderDrillContent() : (isLoading && !rankings ? <LoadingSpinner /> : renderOverview())}
      </div>
    </div>
  );
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center py-16">
    <RefreshCw className="w-8 h-8 animate-spin text-gray-500" />
  </div>
);

const EmptyState = ({ text }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4">
    <Users className="w-12 h-12 text-gray-600 mb-3" />
    <p className="text-gray-500 text-sm">{text}</p>
  </div>
);

export default MobileStaffRankings;
